<template>
  <component
    :is="account !== 'divider' ? 'router-link' : 'div'"
    :to="account !== 'divider' ? offerLink : undefined"
    :class="['account-card', { 'account-card--divider': account === 'divider' }]">
    <template v-if="account === 'divider'">
      <div class="account-card__divider">
        <p class="account-card__divider-title">
          That’s all offers matching your search
        </p>
        <span class="account-card__divider-text">
          We've also included partial matches that might catch your interest 👉
        </span>
      </div>
    </template>
    <template v-else>
      <div class="account-card__header">
        <div class="account-card__header-info">
          <span class="account-card__header-title">
            {{ offerTitle }}
          </span>

          <p class="account-card__header-description">
            {{ account.outer_name }}
          </p>
        </div>
      </div>

      <div
        class="account-card__screenshot"
        @click.stop.prevent="openLightbox">
        <img
          v-if="offerScreenshot"
          :src="offerScreenshot"
          class="account-card__screenshot-image lazyload"
          alt="Screenshot"
          width="100%"
          height="auto">

        <img
          v-else
          src="/images/icons/picture.svg"
          class="account-card__screenshot-icon"
          alt="No Screenshots"
          width="32"
          height="32">

        <div
          v-if="shouldShowScreenshotsCounter"
          class="account-card__screenshot-count">
          {{ account.screenshots.length }}

          <span class="account-card__screenshot-count-icon" />
        </div>
      </div>

      <div class="account-card__tags">
        <account-tag
          v-for="tag in offerTags"
          :key="tag.title"
          :tag="tag" />
      </div>

      <div class="account-card__footer">
        <span class="account-card__price">
          {{ offerPrice }}
        </span>

        <div
          class="account-card__button button button-main">
          Details

          <img
            src="/images/icons/arrow-left.svg"
            alt="Arrow Right"
            width="20px"
            height="10px"
            class="account-card__button-icon">
        </div>
      </div>

      <div
        v-if="offerScreenshot && isMounted"
        @click.stop.prevent>
        <fs-lightbox
          :toggler="lightboxToggler"
          :slide="lightboxSlide"
          :sources="account.screenshots"
          :exit-fullscreen-on-close="true"
          @wheel.native="zoomImage($event)" />
      </div>
    </template>
  </component>
</template>

<script>
import AccountTag from 'Components/Accounts/AccountTag.vue';
import { mapGetters, mapState } from 'vuex';
import FsLightbox from 'fslightbox-vue';
import FsLightBoxZoomMixin from 'Root/mixins/fsLightBoxZoomMixin';

export default {
  name: 'AccountCard',
  components: {
    AccountTag,
    FsLightbox,
  },

  mixins: [FsLightBoxZoomMixin],

  props: {
    account: {
      type: [Object, String],
      required: true,
    },
  },

  data() {
    return {
      lightboxToggler: false,
      lightboxSlide: 0,
      isMounted: false,
    };
  },

  computed: {
    ...mapState(['activeRegion']),
    ...mapGetters(['calcPriceFromUsd']),

    offerLink() {
      return `${this.$route.path}/${this.account.id}`;
    },

    offerTitle() {
      if (!this.account?.data || !this.account?.data_fields) {
        return '';
      }

      const primaryFieldIds = Object.entries(this.account.data_fields)
        // eslint-disable-next-line no-unused-vars
        .filter(([id, field]) => field.is_primary)
        // eslint-disable-next-line no-unused-vars
        .sort(([id1, field1], [id2, field2]) => field2.priority - field1.priority)
        .map(([id]) => id);

      const title = [];

      primaryFieldIds.forEach((id) => {
        title.push(this.account.data[id]);
      });

      return title.join(' · ');
    },

    offerTags() {
      const nonPrimaryFieldIds = Object.entries(this.account.data_fields)
        // eslint-disable-next-line no-unused-vars
        .filter(([id, field]) => !field.is_primary)
        // eslint-disable-next-line no-unused-vars
        .sort(([id1, field1], [id2, field2]) => field2.priority - field1.priority)
        .slice(0, 4)
        .filter(([id]) => this.getTagValue(id) !== 0)
        .map(([id]) => id);

      return nonPrimaryFieldIds.map((id) => ({
        title: this.account.data_fields[id].title,
        count: this.getTagValue(id),
      }));
    },

    offerPrice() {
      return this.calcPriceFromUsd(this.activeRegion.id, +this.account.price_per_step, undefined, 2, true);
    },

    offerScreenshot() {
      return this.account?.screenshots?.[0];
    },

    shouldShowScreenshotsCounter() {
      return this.account?.screenshots?.length > 1;
    },
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    openLightbox() {
      this.lightboxSlide = 1;
      this.lightboxToggler = !this.lightboxToggler;
    },

    getTagValue(id) {
      if (Array.isArray(this.account.data[id])) {
        return this.account.data[`${id}_count`];
      }

      if (!this.account.data[id]) {
        return 0;
      }

      return this.account.data[id];
    },
  },
};
</script>

<style scoped lang="scss">
.account-card {
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 1px solid $color--basic-menu;
  border-radius: 16px;
  background-color: $color--basic-black;
  transition: 0.3s;

  &:hover {
    background-color: #1b2531;
  }
}

.account-card__header {
  display: flex;
  justify-content: space-between;
}

.account-card__header-title {
  display: block;
  margin-bottom: 8px;
  font-size: 19px;
  line-height: 23px;
  font-weight: 500;
  color: $color--basic-white;
}

.account-card__header-description {
  overflow: hidden;
  display: -webkit-box;
  height: 3lh;
  font-size: 15px;
  line-height: 18px;
  color: $color--basic-white;
  word-break: break-word;
}

.account-card__screenshot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  max-height: 188px;
  overflow: hidden;
  border-radius: 12px;
  background: #{rgba($color--basic-blue, 0.25)};
  aspect-ratio: 16 / 9;
}

.account-card__screenshot-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  object-fit: cover;
}

.account-card__screenshot-icon {
  width: 32px;
  height: 32px;
}

.account-card__screenshot-count {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-white;
  background: $color--basic-menu;
}

.account-card__screenshot-count-icon {
  margin-left: 6px;
  width: 16px;
  height: 16px;
  background-color: $color--basic-white;
  -webkit-mask: url('/images/icons/picture-white.svg') no-repeat;
  mask: url('/images/icons/picture-white.svg') no-repeat;
}

.account-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.account-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 16px;
}

.account-card__price {
  font-size: 25px;
  font-weight: 500;
  color: $color--basic-white;
}

.account-card__button {
  display: flex;
  align-items: center;
  padding: 15px 12px;
  border-radius: 5px;
  font-size: 15px;
  line-height: 18px;
  font-weight: 500;
  color: $color--basic-white;
}

.account-card__button-icon {
  width: 24px;
  margin-left: 8px;
  transform: rotate(180deg);
  object-fit: cover;
}

.account-card--divider {
  position: relative;
  min-height: 450px;
  overflow: hidden;
  background: url("/images/catalog/account-divider-image.png") no-repeat right top;

  &::before {
    content: '';
    position: absolute;
    bottom: -160px;
    right: -160px;
    width: 320px;
    height: 320px;
    border-radius: 324.921px;
    background: linear-gradient(270deg, #5F73FA 0%, #7B61FF 100%);
    filter: blur(100px);
  }
}

.account-card__divider {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  color: $color--basic-white;
}

.account-card__divider-title {
  font-weight: 500;
  font-size: $text-19px;
}

.account-card__divider-text {
  font-weight: 400;
  font-size: $text-15px;
}
</style>
